import { endpoints } from "../config/api";
import { getAuthHeaders } from "./getAuthHeaders";

/**
 * Client for the research-DAG planner agent (`POST /research-dag/planner`).
 *
 * The endpoint streams a tag-based wire protocol (the same family the chat agent
 * uses) rather than discrete JSON objects, so the body has to be parsed
 * incrementally as bytes arrive:
 *
 *   <task><think>…</think><tool><s>SearchAgents: …</s></tool>
 *   <tool><e>SearchAgents</e></tool></task>
 *   <ans>…</ans><template>{…}</template><eos></eos>
 *
 * `<think>` streams token by token; `<ans>` / `<template>` arrive as single
 * chunks. `<error>` replaces the tail of a run. Reading always stops at `<eos>`.
 *
 * The endpoint is stateless: every call carries the whole conversation plus the
 * plan built so far, so the caller owns the session.
 */

/** Tags the protocol defines — anything else inside a body is literal text. */
const KNOWN_TAGS = new Set(["task", "think", "tool", "s", "e", "ans", "template", "eos", "error"]);

/** Tags whose inner text we accumulate. */
const TEXT_TAGS = ["think", "ans", "template", "error", "s", "e"];

/**
 * Incremental parser. Feed it decoded chunks with `push()`; it invokes the
 * handlers as tags open and close. Call `end()` once the body is exhausted to
 * flush whatever is left. `isDone()` turns true at `<eos>`.
 *
 * Handlers (all optional):
 *   onTaskStart() / onTaskEnd()
 *   onThink(delta, full) / onThinkEnd(full)
 *   onToolStart(label) / onToolEnd(label)
 *   onAnswer(delta, full) / onAnswerEnd(full)
 *   onTemplate(jsonText) — raw inner text of `<template>`, ready to JSON.parse
 *   onError(message) / onDone()
 */
export function createTagStreamParser(handlers = {}) {
  let buf = "";
  let done = false;
  const stack = [];
  const acc = {};
  TEXT_TAGS.forEach(t => { acc[t] = ""; });

  const current = () => stack[stack.length - 1] || null;

  const emitText = (chunk) => {
    if (!chunk) return;
    const tag = current();
    if (!tag || !(tag in acc)) return;   // stray text between tags — nothing to attach it to
    acc[tag] += chunk;
    if (tag === "think") handlers.onThink?.(chunk, acc.think);
    else if (tag === "ans") handlers.onAnswer?.(chunk, acc.ans);
  };

  const open = (name) => {
    if (name in acc) acc[name] = "";
    stack.push(name);
    if (name === "task") handlers.onTaskStart?.();
    if (name === "eos") { done = true; handlers.onDone?.(); }
  };

  const close = (name) => {
    const at = stack.lastIndexOf(name);
    if (at >= 0) stack.length = at;      // tolerate an unbalanced inner tag
    switch (name) {
      case "think": handlers.onThinkEnd?.(acc.think); break;
      case "s": handlers.onToolStart?.(acc.s.trim()); break;
      case "e": handlers.onToolEnd?.(acc.e.trim()); break;
      case "ans": handlers.onAnswerEnd?.(acc.ans.trim()); break;
      case "template": handlers.onTemplate?.(acc.template.trim()); break;
      case "error": handlers.onError?.(acc.error.trim()); break;
      case "task": handlers.onTaskEnd?.(); break;
      default: break;
    }
  };

  /** A complete tag: `<think>`, `</think>`, `<eos/>`. */
  const TAG_RE = /^<\s*(\/?)\s*([a-zA-Z]+)\s*(\/?)\s*>/;
  /** Could still become a tag once more bytes arrive (`<`, `</tem`, …). */
  const PARTIAL_TAG_RE = /^<\s*\/?\s*[a-zA-Z]*\s*\/?\s*$/;

  /**
   * `flush` = the stream ended, so a trailing `<` that never became a tag is
   * plain text rather than markup we are still waiting for.
   *
   * A `<` that does not start a well-formed tag is emitted as a single character
   * of content and scanning continues from the next one — prose and plan JSON do
   * contain them ("P/E < 15"), and consuming through the next `>` would swallow
   * the real tag that follows.
   */
  const drain = (flush) => {
    for (;;) {
      const lt = buf.indexOf("<");
      if (lt < 0) { emitText(buf); buf = ""; return; }
      if (lt > 0) { emitText(buf.slice(0, lt)); buf = buf.slice(lt); }

      const match = TAG_RE.exec(buf);
      if (!match) {
        if (!flush && PARTIAL_TAG_RE.test(buf)) return;   // tag split across chunks
        emitText("<");
        buf = buf.slice(1);
        continue;
      }

      const [literal, closeSlash, rawName, selfSlash] = match;
      const name = rawName.toLowerCase();
      buf = buf.slice(literal.length);

      if (!KNOWN_TAGS.has(name)) { emitText(literal); continue; }
      if (closeSlash) close(name);
      else { open(name); if (selfSlash) close(name); }
    }
  };

  return {
    push(chunk) { if (chunk) { buf += chunk; drain(false); } },
    /** Flush the tail and close anything the server left open (truncated stream). */
    end() {
      drain(true);
      while (stack.length) close(stack[stack.length - 1]);
    },
    isDone() { return done; },
  };
}

/** Best-effort message out of a non-2xx planner response. */
const errorFromResponse = async (res) => {
  let detail = "";
  try {
    const text = await res.text();
    try {
      const json = JSON.parse(text);
      detail = json?.detail || json?.message || json?.error || "";
      if (detail && typeof detail !== "string") detail = JSON.stringify(detail);
    } catch { detail = text.slice(0, 300); }
  } catch { /* body already gone */ }
  if (res.status === 401 || res.status === 403) return "Not authorised to use the planner — sign in again.";
  return detail?.trim() || `Planner request failed (${res.status}).`;
};

/**
 * Streams one planner turn.
 *
 * @param {object}   opts
 * @param {Array}    opts.messages     Full conversation so far; last item is the new user turn.
 * @param {object}   [opts.currentPlan] Plan from the previous turn, so the agent edits instead of rebuilds.
 * @param {AbortSignal} [opts.signal]
 * @param {object}   opts.handlers     See `createTagStreamParser`.
 */
export async function streamPlanner({ messages, currentPlan = null, signal, handlers = {} }) {
  const res = await fetch(endpoints.researchDagPlanner, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
      Accept: "application/x-ndjson",
    },
    body: JSON.stringify({ messages, current_plan: currentPlan || null }),
    signal,
  });

  if (!res.ok) throw new Error(await errorFromResponse(res));
  if (!res.body) throw new Error("This browser cannot read the planner stream.");

  const parser = createTagStreamParser(handlers);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      parser.push(decoder.decode(value, { stream: true }));
      if (parser.isDone()) break;        // `<eos>` — stop reading
    }
  } finally {
    parser.end();
    try { await reader.cancel(); } catch { /* already closed */ }
  }
}
